export type SourceLink = {
  href: string;
  title: string;
};

const SOURCE_BLOCK_RE = /<sources\b[^>]*>[\s\S]*?<\/sources>/gi;
const SOURCE_ANCHOR_RE = /<a\b[^>]*href\s*=\s*["']([^"']+)["'][^>]*>([\s\S]*?)<\/a>/gi;

/** Pull <sources> blocks out of the message so they render below the markdown text. */
export const extractSources = (content: string): { text: string; sources: SourceLink[] } => {
  const sources: SourceLink[] = [];
  const seen = new Set<string>();

  const collectAnchors = (html: string): void => {
    SOURCE_ANCHOR_RE.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = SOURCE_ANCHOR_RE.exec(html)) !== null) {
      const href = match[1].trim();
      const title = match[2].replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
      if (!href) {
        continue;
      }
      const key = `${href}|${title}`;
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      sources.push({ href, title: title || href })
    }
  };

  const text = content.replace(SOURCE_BLOCK_RE, (block) => {
    collectAnchors(block);
    return '';
  });

  return {
    text: text.replace(/\n{3,}/g, '\n\n').trim(),
    sources,
  };
};

/**
 * Converts common inline HTML tags to their Markdown equivalents so the
 * result can be rendered by a Markdown component instead of dangerouslySetInnerHTML.
 *
 * - <br> / <br /> → newline characters
 * - <em>, <i>      → *italic* (Markdown emphasis)
 * - <strong>, <b>   → **bold** (Markdown strong emphasis)
 * - <p>, <span>, <div> wrapper tags → stripped entirely (opening and closing)
 */
export const normalizeInlineHtml = (text: string): string => {
  return text
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<(?:em|i)>([\s\S]*?)<\/(?:em|i)>/gi, '*$1*')
    .replace(/<(?:strong|b)>([\s\S]*?)<\/(?:strong|b)>/gi, '**$1**')
    .replace(/<\/?(?:p|span|div)>/gi, '');
};
